import React, { useState, useRef } from 'react';
import { Button, CircularProgress, Collapse, Divider, IconButton, Paper } from '@mui/material';
import Grid from '@mui/material/Grid2';
import AddIcon from '@mui/icons-material/Add';
import DeleteIcon from '@mui/icons-material/Delete';
import DragIndicatorIcon from '@mui/icons-material/DragIndicator';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import EditIcon from '@mui/icons-material/Edit';
import EditOffIcon from '@mui/icons-material/EditOff';
import FormatIndentDecreaseIcon from '@mui/icons-material/FormatIndentDecrease';
import FormatIndentIncreaseIcon from '@mui/icons-material/FormatIndentIncrease';
import { isSpecialSectionType, SBSection, SourceBook, SPECIAL_SECTION_TYPES } from '@dndModels';
import StringBox from '@components/input/strings/StringBox';
import { v4 as uuidv4 } from 'uuid';
import DescriptionBox from './DescriptionBox';
import { Visibility, VisibilityOff } from '@mui/icons-material';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import ErrorIcon from '@mui/icons-material/Error';
import { DataType, ItemType } from '@dndModels/Types';

import API from '@dndUtilities/system_api';
import SelectStringBox from '@components/input/selects/SelectStringBox';
import CheckBox from '@components/input/CheckBox';
import { ITEM_TYPES } from '@dndModels/Constants';

const api = API.getInstance();

// ---------------------------------------------------------------------------
// Flat representation used internally for all mutations
// ---------------------------------------------------------------------------

interface FlatNode {
  section: SBSection;
  depth: number;
}

function flatten(sections: SBSection[], depth = 0): FlatNode[] {
  const result: FlatNode[] = [];
  for (const s of sections) {
    result.push({ section: s, depth });
    result.push(...flatten(s.subsections, depth + 1));
  }
  return result;
}

function unflatten(nodes: FlatNode[]): SBSection[] {
  function build(startIndex: number, parentDepth: number): { sections: SBSection[]; nextIndex: number } {
    const sections: SBSection[] = [];
    let i = startIndex;
    while (i < nodes.length && nodes[i].depth > parentDepth) {
      if (nodes[i].depth === parentDepth + 1) {
        const s = nodes[i].section.clone();
        i++;
        const { sections: children, nextIndex } = build(i, parentDepth + 1);
        s.subsections = children;
        sections.push(s);
        i = nextIndex;
      } else {
        i++;
      }
    }
    return { sections, nextIndex: i };
  }
  return build(0, -1).sections;
}

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

function computeParentInfo(
  nodes: FlatNode[],
  index: number
): { parent_id: string; parent_type: 'Source Book' | 'SBSection'; parentKey: string } {
  const depth = nodes[index].depth;
  if (depth === 0) {
    return {
      parent_id: nodes[index].section.parent_id ?? '',
      parent_type: 'Source Book',
      parentKey: `sb:${nodes[index].section.parent_id ?? ''}`,
    };
  }
  for (let i = index - 1; i >= 0; i--) {
    if (nodes[i].depth === depth - 1) {
      const pid = nodes[i].section.true_id;
      return { parent_id: pid, parent_type: 'SBSection', parentKey: `section:${pid}` };
    }
  }
  return { parent_id: '', parent_type: 'Source Book', parentKey: 'sb:' };
}

// ---------------------------------------------------------------------------
// SBSectionsEditor
// ---------------------------------------------------------------------------

interface SBSectionsEditorProps {
  sections: SBSection[];
  source_book: SourceBook;
}

export default function SBSectionsEditor({
  sections: initialSections,
  source_book,
}: SBSectionsEditorProps) {
  const [nodes, setNodes] = useState<FlatNode[]>(() => flatten(initialSections));
  const [expandedIds, setExpandedIds] = useState<Set<string>>(new Set());
  const [editingIds, setEditingIds] = useState<Set<string>>(new Set());
  // true_ids currently being loaded via getFullObject
  const [loadingIds, setLoadingIds] = useState<Set<string>>(new Set());
  // true_id -> original SBSection snapshot for Cancel
  const [dirtyMap, setDirtyMap] = useState<Map<string, SBSection>>(new Map());
  // true_ids currently being saved
  const [savingIds, setSavingIds] = useState<Set<string>>(new Set());
  // true_ids with a most-recent save error
  const [errorIds, setErrorIds] = useState<Set<string>>(new Set());

  const dragOverIndex = useRef<number | null>(null);

  /**
   * Save the source book's section structure using the provided nodes array.
   * Always rebuilds from the given nodes so we never use a stale snapshot.
   */
  const saveStructure = (currentNodes: FlatNode[]) => {
    // debugger;
    source_book.sections = unflatten(currentNodes);
    api.updateObject("source_book", source_book, ["sections"]);
  };

  // ---------------------------------------------------------------------------
  // Expand / edit toggles
  // ---------------------------------------------------------------------------

  const toggleExpand = (id: string) =>
    setExpandedIds(prev => { const n = new Set(prev); n.has(id) ? n.delete(id) : n.add(id); return n; });

  const expand = (id: string) =>
    setExpandedIds(prev => new Set(prev).add(id));

  /**
   * Open the edit panel for a section. If the section hasn't been fully loaded
   * yet, fire getFullObject first and copy the result into the node before opening.
   */
  const openEditing = (tid: string, index: number) => {
    const section = nodes[index].section;

    // Already loaded — open immediately
    if (section.loaded) {
      setEditingIds(prev => new Set(prev).add(tid));
      return;
    }

    // Not yet loaded — fetch, then copy result into the node and open
    setLoadingIds(prev => new Set(prev).add(tid));
    api.getFullObject<SBSection>('sb_section' as DataType, section._id).then(from_db => {
      if (from_db) {
        section.copy(from_db);
        setNodes(prev => {
          const next = [...prev];
          next[index] = { ...next[index], section };
          return next;
        });
      }
      setLoadingIds(prev => { const n = new Set(prev); n.delete(tid); return n; });
      setEditingIds(prev => new Set(prev).add(tid));
    });
  };

  const closeEditing = (tid: string) =>
    setEditingIds(prev => { const n = new Set(prev); n.delete(tid); return n; });

  const toggleEditing = (tid: string, index: number) => {
    if (editingIds.has(tid)) {
      closeEditing(tid);
    } else {
      openEditing(tid, index);
    }
  };

  // ---------------------------------------------------------------------------
  // Dirty tracking
  // ---------------------------------------------------------------------------

  const markDirty = (id: string, original: SBSection) =>
    setDirtyMap(prev => {
      if (prev.has(id)) return prev;
      const next = new Map(prev);
      next.set(id, original.clone());
      return next;
    });

  const clearDirty = (id: string) =>
    setDirtyMap(prev => { const n = new Map(prev); n.delete(id); return n; });

  const isDirty = (id: string) => dirtyMap.has(id);

  // ---------------------------------------------------------------------------
  // API save
  // ---------------------------------------------------------------------------

  /**
   * Full upsert — used when creating new sections or saving name/content.
   * Handles swapping the placeholder true_id for the real _id on creation.
   * Returns the final nodes array after any id-swap so callers can use it
   * immediately (e.g. to call saveStructure with the correct ids).
   */
  const saveSectionContent = async (section: SBSection): Promise<{ saved: SBSection | null; finalNodes: FlatNode[] }> => {
    const tid = section.true_id;
    setSavingIds(prev => new Set(prev).add(tid));
    setErrorIds(prev => { const n = new Set(prev); n.delete(tid); return n; });
    try {
      const result = await api.upsertObject('sb_section' as DataType, section);
      const saved = (result?.res ?? result) as SBSection;
      if (!saved) throw new Error('No saved object returned');

      // New section: swap placeholder true_id for real _id everywhere
      let finalNodes: FlatNode[] = [];
      if (section._id === '') {
        setNodes(prev => {
          const next = prev.map(n =>
            n.section.true_id === tid ? { ...n, section: saved } : n
          );
          finalNodes = next;
          return next;
        });
        setExpandedIds(prev => {
          if (!prev.has(tid)) return prev;
          const n = new Set(prev); n.delete(tid); n.add(saved._id); return n;
        });
        setEditingIds(prev => {
          if (!prev.has(tid)) return prev;
          const n = new Set(prev); n.delete(tid); n.add(saved._id); return n;
        });
      } else {
        // Existing section: update the node in place so the latest data is reflected
        setNodes(prev => {
          const next = prev.map(n =>
            n.section.true_id === tid ? { ...n, section: saved } : n
          );
          finalNodes = next;
          return next;
        });
      }

      clearDirty(saved._id || tid);
      return { saved, finalNodes };
    } catch {
      setErrorIds(prev => new Set(prev).add(tid));
      return { saved: null, finalNodes: nodes };
    } finally {
      setSavingIds(prev => { const n = new Set(prev); n.delete(tid); return n; });
    }
  };

  // ---------------------------------------------------------------------------
  // Content mutations — mark dirty only, no save yet
  // ---------------------------------------------------------------------------

  const updateNode = (index: number, updated: SBSection) => {
    setNodes(prev => {
      const next = [...prev];
      next[index] = { ...next[index], section: updated };
      return next;
    });
  };

  // ---------------------------------------------------------------------------
  // Structural mutations
  // ---------------------------------------------------------------------------

  const addSection = async () => {
    const s = new SBSection({ true_id: uuidv4() });
    s.parent_id = source_book._id;
    s.parent_type = 'Source Book';
    s.position = nodes.filter(n => n.depth === 0).length;
    const { saved: s2, finalNodes } = await saveSectionContent(s);
    if (s2) {
      const next = [...finalNodes, { section: s2, depth: 0 }];
      setNodes(next);
      expand(s2.true_id);
      setEditingIds(prev => new Set(prev).add(s2.true_id));
      saveStructure(next);
    }
  };

  /**
   * Something went wrong with a previous version of saving structure 
   * and it removed the entire section structure from the source book.
   * Fortunately, each section individually knows its parents, so we can get them back.
   */
  const missingSections = async () => {
    const all_sections = await api.getObjects<SBSection>({ data_type: "sb_section" });
    const root_sections = all_sections.filter(o => o.parent_type === "Source Book" && o.parent_id === source_book._id);
    const getSubsections = (section: SBSection): SBSection[] => {
      const subsections = all_sections.filter(o => o.parent_type === "SBSection" && o.parent_id === section._id);
      for (const section2 of subsections) {
        section2.subsections = getSubsections(section2);
      }
      return subsections;
    }
    // debugger;
    for (const section of root_sections) {
      section.subsections = getSubsections(section);
    }
    source_book.sections = root_sections;
    // Now save the structure to the source book.
    api.updateObject("source_book", source_book, ["sections"]);
    setNodes(flatten(root_sections));
  };

  const deleteNode = async (index: number) => {
    setNodes(prev => {
      const targetDepth = prev[index].depth;
      let end = index + 1;
      while (end < prev.length && prev[end].depth > targetDepth) end++;

      prev.slice(index, end).forEach(n => {
        if (n.section._id !== '') {
          api.deleteObject('sb_section' as DataType, n.section);
        }
        const tid = n.section.true_id;
        setExpandedIds(p => { const x = new Set(p); x.delete(tid); return x; });
        setEditingIds(p => { const x = new Set(p); x.delete(tid); return x; });
        clearDirty(tid);
      });

      const remaining = [...prev.slice(0, index), ...prev.slice(end)];
      saveStructure(remaining);
      return remaining;
    });
  };

  const addChildSection = async (parentIndex: number) => {
    const prev = nodes;
    const parentDepth = prev[parentIndex].depth;
    let insertAt = parentIndex + 1;
    while (insertAt < prev.length && prev[insertAt].depth > parentDepth) insertAt++;

    const siblingCount = prev
      .slice(parentIndex + 1, insertAt)
      .filter(n => n.depth === parentDepth + 1).length;

    const s = new SBSection({ true_id: uuidv4() });
    s.parent_id = prev[parentIndex].section.true_id;
    s.parent_type = 'SBSection';
    s.position = siblingCount;

    const { saved: s2, finalNodes } = await saveSectionContent(s);
    if (!s2) return;

    // Recompute insertAt against finalNodes in case ids shifted
    const parentTid = prev[parentIndex].section.true_id;
    const newParentIndex = finalNodes.findIndex(n => n.section.true_id === parentTid);
    const newParentDepth = finalNodes[newParentIndex]?.depth ?? parentDepth;
    let newInsertAt = newParentIndex + 1;
    while (newInsertAt < finalNodes.length && finalNodes[newInsertAt].depth > newParentDepth) newInsertAt++;

    const next = [
      ...finalNodes.slice(0, newInsertAt),
      { section: s2, depth: newParentDepth + 1 },
      ...finalNodes.slice(newInsertAt),
    ];

    setNodes(next);
    expand(finalNodes[newParentIndex].section.true_id);
    expand(s2.true_id);
    setEditingIds(p => new Set(p).add(s2.true_id));
    saveStructure(next);
  };

  const indentNode = (index: number) => {
    setNodes(prev => {
      if (index === 0) return prev;
      const currentDepth = prev[index].depth;
      let newParentIndex = index - 1;
      while (newParentIndex >= 0 && prev[newParentIndex].depth > currentDepth) newParentIndex--;
      if (newParentIndex < 0 || prev[newParentIndex].depth !== currentDepth) return prev;

      let end = index + 1;
      while (end < prev.length && prev[end].depth > currentDepth) end++;
      const moving = prev.slice(index, end).map(n => ({ ...n, depth: n.depth + 1 }));

      let insertAt = newParentIndex + 1;
      while (insertAt < index && prev[insertAt].depth > prev[newParentIndex].depth) insertAt++;
      const next = [...prev.slice(0, index), ...prev.slice(end)];
      next.splice(newParentIndex + 1 + (insertAt - (newParentIndex + 1)), 0, ...moving);
      saveStructure(next);
      return next;
    });
  };

  const outdentNode = (index: number) => {
    setNodes(prev => {
      const currentDepth = prev[index].depth;
      if (currentDepth === 0) return prev;

      let end = index + 1;
      while (end < prev.length && prev[end].depth > currentDepth) end++;
      const moving = prev.slice(index, end).map(n => ({ ...n, depth: n.depth - 1 }));

      const next = [...prev.slice(0, index), ...prev.slice(end)];
      const removedCount = end - index;
      let insertAt = end - removedCount;
      while (insertAt < next.length && next[insertAt].depth > currentDepth - 1) insertAt++;
      next.splice(insertAt, 0, ...moving);
      saveStructure(next);
      return next;
    });
  };

  // ---------------------------------------------------------------------------
  // Drag and drop
  // ---------------------------------------------------------------------------

  const handleDragStart = (e: React.DragEvent, index: number) => {
    e.dataTransfer.setData('text/plain', String(index));
    e.dataTransfer.effectAllowed = 'move';
  };

  const handleDragOver = (e: React.DragEvent, index: number) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
    dragOverIndex.current = index;
  };

  const handleDrop = (e: React.DragEvent, toIndex: number) => {
    e.preventDefault();
    e.stopPropagation();
    const fromIndex = Number(e.dataTransfer.getData('text/plain'));
    if (fromIndex === toIndex) return;

    setNodes(prev => {
      const fromDepth = prev[fromIndex].depth;
      let fromEnd = fromIndex + 1;
      while (fromEnd < prev.length && prev[fromEnd].depth > fromDepth) fromEnd++;
      const moving = prev.slice(fromIndex, fromEnd);

      const next = [...prev.slice(0, fromIndex), ...prev.slice(fromEnd)];
      const adjustedTo = toIndex > fromIndex ? toIndex - (fromEnd - fromIndex) : toIndex;
      next.splice(Math.max(0, adjustedTo), 0, ...moving);
      saveStructure(next);
      return next;
    });
    dragOverIndex.current = null;
  };

  // ---------------------------------------------------------------------------
  // Save / Cancel for content edits
  // ---------------------------------------------------------------------------

  const handleSave = async (index: number) => {
    const node = nodes[index];
    const s = node.section.clone();
    const { parent_id, parent_type, parentKey } = computeParentInfo(nodes, index);
    s.parent_id = parent_type === 'Source Book' ? source_book._id : parent_id;
    s.parent_type = parent_type;
    let sibPos = 0;
    for (let i = 0; i < index; i++) {
      if (computeParentInfo(nodes, i).parentKey === parentKey) sibPos++;
    }
    s.position = sibPos;
    const { finalNodes } = await saveSectionContent(s);
    // After content save, also persist structure so the source book reflects
    // the latest section data (name, special_type, etc.) in its sections array.
    saveStructure(finalNodes);
  };

  const handleCancel = (index: number) => {
    const tid = nodes[index].section.true_id;
    const original = dirtyMap.get(tid);
    if (original) {
      setNodes(prev => {
        const next = [...prev];
        next[index] = { ...next[index], section: original.clone() };
        return next;
      });
      clearDirty(tid);
    }
  };

  // ---------------------------------------------------------------------------
  // Render
  // ---------------------------------------------------------------------------

  return (
    <div>
      {nodes.length === 0 && (
        <span style={{ color: '#888' }}>No sections.</span>
      )}
      {nodes.map((node, i) => {
        const { section, depth } = node;
        const tid = section.true_id;
        const expanded = expandedIds.has(tid);
        const editing = editingIds.has(tid);
        const loadingEdit = loadingIds.has(tid);
        const dirty = isDirty(tid);
        const saving = savingIds.has(tid);
        const hasError = errorIds.has(tid);
        const structuralDisabled = dirty || saving;

        // Hide if any ancestor is collapsed
        if (depth > 0) {
          let j = i - 1;
          while (j >= 0 && nodes[j].depth >= depth) j--;
          if (j >= 0 && !expandedIds.has(nodes[j].section.true_id)) return null;
          let ancestor = j;
          while (ancestor > 0) {
            let k = ancestor - 1;
            while (k >= 0 && nodes[k].depth >= nodes[ancestor].depth) k--;
            if (k < 0) break;
            if (!expandedIds.has(nodes[k].section.true_id)) return null;
            ancestor = k;
          }
        }

        const canIndent = !structuralDisabled && i > 0 && (() => {
          let j = i - 1;
          while (j >= 0 && nodes[j].depth > depth) j--;
          return j >= 0 && nodes[j].depth === depth;
        })();
        const canOutdent = !structuralDisabled && depth > 0;

        let childCount = 0;
        for (let j = i + 1; j < nodes.length && nodes[j].depth > depth; j++) {
          if (nodes[j].depth === depth + 1) childCount++;
        }

        const renderSpecialSectionOptions = () => {
          switch (section.special_type) {
            case "Items": 
              if (true) {
                const special_attributes = section.special_attributes as { item_types?: ItemType[], magic?: boolean };
                const item_types = special_attributes.item_types ?? [];
                const magic = special_attributes.magic;
                return (
                  <>
                    <Grid size={{ xs: 6 }}>
                      <SelectStringBox
                        name="Item Types"
                        options={ITEM_TYPES}
                        values={item_types}
                        multiple
                        onChange={(changed: ItemType[]) => {
                          const s = section.clone();
                          s.special_attributes.item_types = changed;
                          markDirty(tid, section);
                          updateNode(i, s);
                        }}
                      />
                    </Grid>
                    <Grid size={{ xs: 6 }}>
                      <SelectStringBox
                        name="Magic"
                        options={["Yes", "No", "Ignore"]}
                        value={magic === undefined ? "Ignore" : (magic ? "Yes" : "No")}
                        onChange={(changed: "Yes" | "No" | "Ignore") => {
                          const s = section.clone();
                          if (changed === "Ignore") {
                            delete s.special_attributes.magic;
                          } else {
                            s.special_attributes.magic = changed === "Yes";
                          }
                          markDirty(tid, section);
                          updateNode(i, s);
                        }}
                      />
                    </Grid>
                  </>
                );
              }
            break;
          }
          return null;
        }

        return (
          <div
            key={tid}
            draggable={!structuralDisabled}
            style={{ marginLeft: depth * 24 }}
            onDragStart={e => handleDragStart(e, i)}
            onDragOver={e => handleDragOver(e, i)}
            onDrop={e => handleDrop(e, i)}
          >
            <Paper variant="outlined" style={{ marginBottom: 8, overflow: 'hidden' }}>
              {/* Header */}
              <Grid container spacing={1} alignItems="center" direction="row" style={{ padding: '4px 8px' }}>
                <Grid style={{ display: 'flex', alignItems: 'center' }}>
                  <DragIndicatorIcon
                    fontSize="small"
                    color={structuralDisabled ? 'disabled' : 'action'}
                    style={{ cursor: structuralDisabled ? 'not-allowed' : 'grab' }}
                  />
                </Grid>

                <Grid size={{ xs: 12 }} style={{ cursor: 'pointer' }} onClick={() => toggleExpand(tid)}>
                  <span className="header">
                    {section.name || <em style={{ color: '#888' }}>Untitled Section</em>}
                  </span>
                </Grid>

                <Grid onClick={e => e.stopPropagation()} style={{ display: 'flex' }}>
                  <IconButton size="small" title="Outdent" disabled={!canOutdent} onClick={() => outdentNode(i)}>
                    <FormatIndentDecreaseIcon fontSize="small" />
                  </IconButton>
                  <IconButton size="small" title="Indent (make child of section above)" disabled={!canIndent} onClick={() => indentNode(i)}>
                    <FormatIndentIncreaseIcon fontSize="small" />
                  </IconButton>
                  <IconButton size="small" title="Delete" disabled={saving} onClick={() => deleteNode(i)}>
                    <DeleteIcon fontSize="small" />
                  </IconButton>
                </Grid>
                
                <Grid style={{ display: 'flex', alignItems: 'center' }}>
                  <IconButton
                    size="small"
                    title={editing ? 'Hide edit controls' : 'Show edit controls'}
                    disabled={loadingEdit}
                    onClick={() => toggleEditing(tid, i)}
                  >
                    {loadingEdit
                      ? <CircularProgress size={16} />
                      : editing ? <EditOffIcon fontSize="small" /> : <EditIcon fontSize="small" />}
                  </IconButton>
                  <span
                    style={{ display: 'flex', alignItems: 'center', cursor: 'pointer' }}
                    onClick={() => toggleExpand(tid)}
                  >
                    <span style={{ fontSize: '0.75rem', color: '#888', marginRight: 2 }}>
                      {childCount} Subsection{childCount === 1 ? '' : 's'}
                    </span>
                    <IconButton size="small" title={expanded ? 'Collapse subsections' : 'Expand subsections'}>
                      {expanded ? <ExpandLessIcon fontSize="small" /> : <ExpandMoreIcon fontSize="small" />}
                    </IconButton>
                  </span>
                </Grid>
              </Grid>

              {/* Edit controls */}
              <Collapse in={editing && !loadingEdit}>
                <Divider />
                <div style={{ padding: '8px 12px' }}>
                  <Grid container style={{ marginBottom: 8 }}>
                    <Grid size={{ xs: 4 }}>
                      <StringBox
                        name="Section Name"
                        value={section.name}
                        onBlur={(val: string) => {
                          const s = section.clone();
                          s.name = val;
                          markDirty(tid, section);
                          updateNode(i, s);
                        }}
                      />
                    </Grid>
                    <Grid size={{ xs: 4 }}>
                      <SelectStringBox
                        name="Special Section Type"
                        options={SPECIAL_SECTION_TYPES}
                        value={section.special_type}
                        onChange={(val: string) => {
                          const s = section.clone();
                          if (isSpecialSectionType(val)) {
                            s.special_type = val;
                          }
                          markDirty(tid, section);
                          updateNode(i, s);
                        }}
                      />
                    </Grid>
                    <Grid size={{ xs: 4 }}>
                      <CheckBox
                        name="Single Page"
                        value={section.single_page}
                        onChange={(val) => {
                          const s = section.clone();
                          s.single_page = val;
                          markDirty(tid, section);
                          updateNode(i, s);
                        }}
                      />
                    </Grid>
                    {renderSpecialSectionOptions()}
                  </Grid>
                  <Grid container style={{ marginBottom: 8 }}>
                    <Grid size={{ xs: 12 }}>
                      <DescriptionBox
                        name="Content"
                        value={section.content}
                        onChange={(val: string) => {
                          const s = section.clone();
                          s.content = val;
                          markDirty(tid, section);
                          updateNode(i, s);
                        }}
                      />
                    </Grid>
                  </Grid>

                  {/* Save status + buttons */}
                  <Grid container alignItems="center" spacing={1} style={{ marginBottom: 8 }}>
                    <Grid style={{ display: 'flex', alignItems: 'center', minWidth: 24 }}>
                      {saving ? (
                        <CircularProgress size={16} />
                      ) : hasError ? (
                        <ErrorIcon fontSize="small" color="error" titleAccess="Save failed" />
                      ) : !dirty ? (
                        <CheckCircleIcon fontSize="small" color="success" titleAccess="Saved" />
                      ) : null}
                    </Grid>
                    <Grid>
                      <span style={{ fontSize: '0.75rem', color: saving ? '#888' : hasError ? 'red' : dirty ? '#888' : 'green' }}>
                        {saving ? 'Saving…' : hasError ? 'Save failed' : dirty ? 'Unsaved changes' : 'Saved'}
                      </span>
                    </Grid>
                    {dirty && !saving && (
                      <>
                        <Grid>
                          <Button size="small" variant="contained" color="primary" onClick={() => handleSave(i)}>
                            Save
                          </Button>
                        </Grid>
                        <Grid>
                          <Button size="small" variant="outlined" onClick={() => handleCancel(i)}>
                            Cancel
                          </Button>
                        </Grid>
                      </>
                    )}
                    {hasError && !saving && (
                      <Grid>
                        <Button size="small" variant="contained" color="error" onClick={() => handleSave(i)}>
                          Retry
                        </Button>
                      </Grid>
                    )}
                  </Grid>
                </div>
              </Collapse>

              {/* Add Subsection */}
              <Collapse in={expanded}>
                <Divider />
                <div style={{ padding: '8px 12px' }}>
                  <Button size="small" startIcon={<AddIcon />} disabled={saving} onClick={() => addChildSection(i)}>
                    Add Subsection
                  </Button>
                </div>
              </Collapse>
            </Paper>
          </div>
        );
      })}

      <p>
        <Button size="small" startIcon={<AddIcon />} onClick={addSection} style={{ marginTop: 4 }}>
          Add Section
        </Button>
      </p>
      <p>
        <Button size="small" startIcon={<AddIcon />} onClick={missingSections} style={{ marginTop: 4 }}>
          Check for Missing Sections
        </Button>
      </p>
    </div>
  );
}